import { useNavigate, Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { openSupportChat } from "@/utils/openSupportChat";
import { Helmet } from "react-helmet-async";
import {
  FileText,
  CheckCircle,
  Shield,
  Clock,
  ArrowRight,
  User,
  DollarSign,
  Calendar,
  ChevronDown,
  ChevronUp,
  FileBarChart,
  Receipt,
  Briefcase,
  HelpCircle,
  Home as HomeIcon,
  Key,
  ClipboardCheck,
  AlertTriangle,
  FileCheck,
  Users,
  Calculator
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// Same reveal hook the home page uses (tiny threshold so tall sections
// still trigger on mobile).
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

const toneCard = {
  mint: { bg: "bg-emerald-400", big: "text-slate-900", small: "text-emerald-950" },
  black: { bg: "bg-[#0b0b0b]", big: "text-white", small: "text-slate-300" },
  gray: { bg: "bg-slate-100", big: "text-slate-900", small: "text-slate-600" },
};

const HERO_STATS = [
  { value: "3x", label: "Typical Income Ratio", tone: "mint" },
  { value: "2-3", label: "Paystubs Required", tone: "black" },
  { value: "5 min", label: "To Create", tone: "gray" },
];

// FAQ Accordion Component
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-3">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
    >
      <span className="font-semibold text-slate-900 pr-4">{question}</span>
      {isOpen ? (
        <ChevronUp className="w-5 h-5 text-emerald-600 flex-shrink-0" />
      ) : (
        <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-5 pb-5">
        <p className="text-slate-600 leading-relaxed">{answer}</p>
      </div>
    )}
  </div>
);

// Internal Link Card Component
const InternalLinkCard = ({ title, description, path, icon: Icon }) => {
  const navigate = useNavigate();
  return (
    <div
      onClick={() => navigate(path)}
      className="group bg-white border border-slate-200 rounded-2xl shadow-sm p-5 cursor-pointer hover:border-emerald-400 hover:shadow-md transition-all duration-300"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h4 className="font-display font-bold text-slate-900 group-hover:text-emerald-700 transition-colors mb-1">{title}</h4>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
        <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
      </div>
    </div>
  );
};

// Income requirement cards — three tones like the About values bento.
const REQUIREMENT_CARDS = [
  {
    icon: Calculator,
    title: "The 3x rule",
    body: "The most common requirement is that your gross monthly income should be at least three times the monthly rent. This is the industry standard for most landlords.",
    exampleLabel: "Example Calculation:",
    exampleText: "$1,500 rent × 3 = $4,500 gross monthly income required",
    tone: "mint",
  },
  {
    icon: Users,
    title: "Combined income",
    body: "When applying with roommates or a partner, landlords typically consider your combined gross income. Each applicant may need to provide their own paystubs.",
    exampleLabel: "Example:",
    exampleText: "Applicant A: $3,000 + Applicant B: $2,500 = $5,500 combined",
    tone: "black",
  },
  {
    icon: FileCheck,
    title: "Documentation period",
    body: "Landlords typically want to see your two to three most recent paystubs. The pay dates should be within the last 30 to 60 days for relevance.",
    exampleLabel: "Standard Request:",
    exampleText: "2-3 recent paystubs from the past 30-60 days",
    tone: "gray",
  },
];

// Application tips — tone rotation avoids same-tone neighbors in the 2-col grid.
const TIPS = [
  {
    icon: FileText,
    title: "Prepare documents in advance",
    body: "Gather all required documentation before you start apartment hunting. Having your paystubs, ID, references, and other documents ready shows landlords you are organized and serious.",
    tone: "gray",
  },
  {
    icon: DollarSign,
    title: "Know your budget",
    body: "Calculate what rent you can afford based on the income shown on your paystubs. Looking at apartments within your qualified range saves time for you and landlords.",
    tone: "mint",
  },
  {
    icon: Calendar,
    title: "Keep paystubs current",
    body: "Make sure your paystubs are recent. Most landlords want to see pay dates within the last 30 to 60 days. Update your documentation regularly during your apartment search.",
    tone: "black",
  },
  {
    icon: User,
    title: "Be honest and accurate",
    body: "Always provide accurate information that reflects your actual income. Landlords verify information, and discrepancies can result in application denial or lease termination.",
    tone: "gray",
  },
  {
    icon: ClipboardCheck,
    title: "Provide additional documentation",
    body: "Strengthen your application by offering additional documents like tax returns or an employment verification letter from your employer.",
    tone: "mint",
  },
  {
    icon: Shield,
    title: "Protect sensitive information",
    body: "Be cautious about sharing sensitive information like your full Social Security number. Many landlords only need the last four digits. Only provide complete information through secure channels.",
    tone: "black",
  },
];

export default function PaystubForApartment() {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(0);

  const [heroRef, heroInView] = useInView();
  const [reqRef, reqInView] = useInView();
  const [tipsRef, tipsInView] = useInView();
  const [faqRef, faqInView] = useInView();
  const [linksRef, linksInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  const faqs = [
    {
      question: "How many paystubs do landlords typically require?",
      answer: "Most landlords request two to three of your most recent paystubs. Some property management companies may ask for up to four paystubs to verify consistent income over a longer period. Having several recent paystubs ready can help speed up your application process."
    },
    {
      question: "What income-to-rent ratio do landlords look for?",
      answer: "The standard guideline is that your gross monthly income should be at least three times the monthly rent. For example, if rent is $1,500 per month, landlords typically want to see gross monthly income of at least $4,500. Some landlords may accept a 2.5x ratio, especially in high-cost areas."
    },
    {
      question: "Can self-employed individuals use paystubs for apartment applications?",
      answer: "Self-employed individuals often face additional scrutiny but can still document their income effectively. Instead of traditional paystubs, they may need to provide tax returns or create income documentation that shows their earnings. Many landlords will accept alternative forms of income verification for freelancers and business owners."
    },
    {
      question: "What if my paystubs show variable income?",
      answer: "If your income varies (common for commission-based jobs, freelancers, or gig workers), landlords may average your earnings over several months. Providing more paystubs or additional documentation like tax returns can help demonstrate your average income over time."
    },
    {
      question: "Do landlords verify paystub information?",
      answer: "Yes, many landlords verify income information by contacting employers, requesting tax returns, or using third-party verification services. Always ensure the information on your paystubs accurately reflects your actual earnings and employment status."
    },
    {
      question: "What other documents might landlords request besides paystubs?",
      answer: "Landlords commonly request additional documentation such as tax returns (W-2s or 1099s), employment verification letters, government-issued ID, rental history, and credit reports. Being prepared with these documents can speed up your application."
    }
  ];

  const internalLinks = [
    {
      title: "Pay Stub Generator",
      description: "Create professional paystubs instantly with accurate tax calculations",
      path: "/app",
      icon: FileText
    },
    {
      title: "W-2 Form Generator",
      description: "Generate IRS-compliant W-2 forms for tax documentation",
      path: "/app/tax-forms",
      icon: Receipt
    },
    {
      title: "1099-NEC Generator",
      description: "Create 1099 forms for contractor and freelance income",
      path: "/app/tax-forms",
      icon: FileBarChart
    },
    {
      title: "How to Make a Paystub",
      description: "Step-by-step guide to creating professional paystubs",
      path: "/how-to-make-a-paystub",
      icon: ClipboardCheck
    },
    {
      title: "Self-Employed Paystub Generator",
      description: "Documentation for freelancers and self-employed individuals",
      path: "/app/paystubs",
      icon: Briefcase
    }
  ];

  // Schema.org structured data for SEO
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Paystub for Apartment: Complete Guide to Proof of Income for Renters",
    "description": "Learn what landlords look for in paystubs when renting an apartment. Understand income requirements, documentation needs, and how to present your financial information professionally.",
    "image": "https://mintslip.com/og-image.png",
    "author": {
      "@type": "Organization",
      "name": "MintSlip"
    },
    "publisher": {
      "@type": "Organization",
      "name": "MintSlip",
      "logo": {
        "@type": "ImageObject",
        "url": "https://mintslip.com/logo.png"
      }
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-07-01"
  };

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Helmet>
        <title>Paystub for Apartment | Proof of Income Guide for Renters 2025 | MintSlip</title>
        <meta name="description" content="Learn how to use paystubs for apartment applications. Understand what landlords look for in proof of income documentation. Create professional paystubs with accurate calculations. Trusted guide for renters." />
        <meta name="keywords" content="paystub for apartment, proof of income for renting, apartment application paystub, landlord income verification, rental income requirements, paystub for rent application, income documentation apartment" />
        <meta property="og:title" content="Paystub for Apartment - Complete Proof of Income Guide for Renters 2025" />
        <meta property="og:description" content="Everything you need to know about using paystubs for apartment applications. Learn income requirements and documentation tips." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://mintslip.com/paystub-for-apartment" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Paystub for Apartment | MintSlip" />
        <meta name="twitter:description" content="Complete guide to proof of income for apartment applications." />
        <link rel="canonical" href="https://mintslip.com/paystub-for-apartment" />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <Header />

      {/* Hero — centered like the home hero, with the stats bento below */}
      <section ref={heroRef} className="relative max-w-[1288px] mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-20">
        <div className={`transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {/* Breadcrumb */}
          <nav className="mb-6 text-sm">
            <ol className="flex items-center justify-center gap-2 text-slate-500">
              <li><Link to="/" className="hover:text-emerald-700 transition-colors">Home</Link></li>
              <li>/</li>
              <li className="text-slate-900 font-medium">Paystub for Apartment</li>
            </ol>
          </nav>

          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-50 rounded-full border border-emerald-200/80 mb-6">
              <HomeIcon className="w-4 h-4 text-emerald-700" />
              <span className="text-sm font-medium text-emerald-900">Complete Renter's Guide</span>
            </div>
          </div>

          <h1 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Paystub for <span className="font-black">apartment</span>
          </h1>
          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Your complete guide to understanding proof of income requirements when renting an apartment. Learn what landlords look for, how to prepare your documentation, and create professional paystubs.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-14">
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create a paystub</span>
            </button>
            <button
              onClick={() => navigate("/how-to-make-a-paystub")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
            >
              Learn How to Make a Paystub
            </button>
          </div>

          {/* Quick stats bento */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5 max-w-3xl mx-auto">
            {HERO_STATS.map((s) => {
              const t = toneCard[s.tone];
              return (
                <div key={s.label} className={`rounded-[28px] ${t.bg} px-4 py-8 md:px-8 md:py-10 flex flex-col items-center justify-center text-center`}>
                  <span className={`font-display font-black text-4xl md:text-5xl ${t.big}`}>{s.value}</span>
                  <span className={`mt-2 text-sm md:text-base ${t.small}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <h2 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-10">
          Why landlords require <span className="font-black">paystubs</span> for apartment applications
        </h2>

        <div className="max-w-3xl mx-auto">
          <p className="text-slate-600 text-lg leading-relaxed mb-6">
            When applying to rent an apartment, one of the most important documents you will need is proof of income. Landlords and property management companies use paystubs to verify that prospective tenants have sufficient and stable income to afford the monthly rent. Understanding what landlords look for in your income documentation can help you prepare a stronger rental application.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-6">
            A <strong>paystub</strong> (also called a pay stub or earnings statement) provides detailed information about your earnings, including your gross pay, tax withholdings, deductions, and net pay. This document gives landlords confidence that you have a reliable source of income and can consistently meet your monthly rent obligations.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-8">
            Whether you are a traditional employee, freelancer, contractor, or self-employed individual, having proper income documentation is essential for securing the apartment you want. This guide will walk you through everything you need to know about using paystubs for apartment applications.
          </p>

          <div className="rounded-[28px] bg-emerald-400 p-8 md:p-10 mb-6">
            <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mb-5">What landlords typically look for</h3>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-emerald-950">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-950 flex-shrink-0" />
                <span>Consistent income over multiple pay periods</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-950 flex-shrink-0" />
                <span>Gross income that meets minimum requirements</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-950 flex-shrink-0" />
                <span>Employer information and verification</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-950 flex-shrink-0" />
                <span>Year-to-date earnings history</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-950 flex-shrink-0" />
                <span>Recent pay dates (within 30 days)</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-950 flex-shrink-0" />
                <span>Professional formatting and completeness</span>
              </li>
            </ul>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900 mb-1">Important disclaimer</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  MintSlip is intended for lawful and legitimate use only. Users are responsible for ensuring all information entered is accurate and reflects their actual income. Misrepresenting income on rental applications may constitute fraud and can result in legal consequences. Always provide truthful information to landlords and property managers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Income Requirements Section */}
      <section ref={reqRef} className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${reqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Understanding <span className="font-black">income requirements</span> for renting
        </h2>
        <p className={`text-center text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${reqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Most landlords follow standard income-to-rent ratios when evaluating applications. Understanding these requirements helps you know if you qualify.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {REQUIREMENT_CARDS.map((c, i) => {
            const t = toneCard[c.tone];
            const dark = c.tone === "black";
            return (
              <div
                key={c.title}
                className={`rounded-[28px] ${t.bg} p-8 md:p-10 transition-all duration-700 ${reqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: `${150 + i * 120}ms` }}
              >
                <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
                  <c.icon className="w-7 h-7 text-slate-900" />
                </span>
                <h3 className={`font-display text-2xl md:text-3xl font-medium mb-3 ${t.big}`}>{c.title}</h3>
                <p className={`leading-relaxed mb-5 ${t.small}`}>{c.body}</p>
                <div className={`rounded-2xl p-4 ${dark ? "bg-white/10" : "bg-white"}`}>
                  <p className={`text-sm mb-1 ${dark ? "text-slate-400" : "text-slate-500"}`}>{c.exampleLabel}</p>
                  <p className={`font-medium ${dark ? "text-white" : "text-slate-800"}`}>{c.exampleText}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* What Paystubs Show Section */}
      <section className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <h2 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-10">
          What information landlords review on <span className="font-black">your paystub</span>
        </h2>

        <article className="max-w-3xl mx-auto">
          <p className="text-slate-600 text-lg leading-relaxed mb-6">
            When a landlord or property manager reviews your paystub, they are looking for specific information that helps them assess your ability to pay rent consistently. Understanding what they examine can help you ensure your documentation is complete and professional.
          </p>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">Gross income vs. net income</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            <strong>Gross income</strong> is your total earnings before any taxes or deductions are taken out. This is typically the figure landlords use when calculating whether you meet their income requirements. Your gross pay shows your full earning potential and is the standard measure used in rental qualifications.
          </p>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            <strong>Net income</strong> (or take-home pay) is what remains after all taxes and deductions. While some landlords may consider net income, the industry standard is to base requirements on gross income. Make sure your paystubs clearly show both figures for transparency.
          </p>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">Employer information</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            Your paystub should include your employer's name, address, and contact information. Many landlords verify employment by calling the employer directly or using third-party verification services. Having complete employer information on your paystubs facilitates this verification process.
          </p>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">Pay period and frequency</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            The pay period dates and pay frequency (weekly, biweekly, semi-monthly, or monthly) help landlords calculate your monthly income accurately. For example, if you are paid biweekly, landlords will multiply your gross pay by 26 and divide by 12 to determine your average monthly income.
          </p>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">Year-to-date (YTD) information</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            YTD figures show your cumulative earnings from January 1st through the current pay period. This information helps landlords see your income history and verify consistency over time. Strong YTD numbers can strengthen your application, especially if you have been employed for several months.
          </p>

          <div className="rounded-[28px] bg-[#0b0b0b] p-8 md:p-10 my-10">
            <div className="flex items-center gap-4 mb-6">
              <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                <Key className="w-7 h-7 text-slate-900" />
              </span>
              <h4 className="font-display text-2xl md:text-3xl font-medium text-white">Key paystub elements for apartment applications</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                  <span>Employee name and address</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                  <span>Employer name and address</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                  <span>Pay period start and end dates</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                  <span>Pay date</span>
                </li>
              </ul>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                  <span>Gross earnings</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                  <span>Deductions and taxes</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                  <span>Net pay</span>
                </li>
                <li className="flex items-start gap-2 text-slate-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400 mt-1 flex-shrink-0" />
                  <span>Year-to-date totals</span>
                </li>
              </ul>
            </div>
          </div>
        </article>
      </section>

      {/* Different Worker Types Section */}
      <section className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <h2 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-10">
          Income documentation for different <span className="font-black">employment types</span>
        </h2>

        <article className="max-w-3xl mx-auto">
          <p className="text-slate-600 text-lg leading-relaxed mb-6">
            Not everyone receives traditional paystubs from an employer. The type of income documentation you need may vary based on your employment situation. Here is how different worker types can document their income for apartment applications.
          </p>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">Traditional W-2 employees</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            If you work for an employer who withholds taxes from your paycheck, you are a W-2 employee. Your paystubs will show gross wages, federal and state tax withholdings, Social Security and Medicare taxes, and any benefit deductions. This is the most straightforward documentation for landlords to review.
          </p>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">Self-employed and freelancers</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            Self-employed individuals and freelancers may not receive traditional paystubs. In these cases, landlords typically accept alternative documentation such as tax returns from the previous year, profit and loss statements, or 1099 forms. Some self-employed individuals create their own income statements to document their earnings professionally.
          </p>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">1099 contractors</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            Independent contractors who receive 1099 forms do not have taxes withheld from their payments. Their payment statements look different from traditional W-2 paystubs. Landlords reviewing contractor income will want to see consistent payment history and may request additional documentation like contracts to verify income stability.
          </p>

          <h3 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mt-10 mb-4">Multiple income sources</h3>

          <p className="text-slate-600 text-lg leading-relaxed mb-4">
            If you have multiple jobs or income sources, you can provide paystubs from each source. Landlords will add up your total income when determining if you meet their requirements. Having documentation ready from all income sources can strengthen your application and demonstrate financial stability.
          </p>

          <div className="rounded-[28px] bg-emerald-400 p-8 md:p-10 my-10">
            <h4 className="font-display text-2xl md:text-3xl font-medium text-slate-900 mb-3">Create professional income documentation</h4>
            <p className="text-emerald-950 leading-relaxed mb-6">
              MintSlip helps you create professional paystubs with accurate calculations. Our templates are designed to include all the information landlords look for when reviewing rental applications.
            </p>
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create Your Paystub Now</span>
            </button>
          </div>
        </article>
      </section>

      {/* Tips Section */}
      <section ref={tipsRef} className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${tipsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Tips for a successful <span className="font-black">apartment application</span>
        </h2>
        <p className={`text-center text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${tipsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Follow these best practices to strengthen your rental application and improve your chances of approval.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {TIPS.map((tip, i) => {
            const t = toneCard[tip.tone];
            return (
              <div
                key={tip.title}
                className={`rounded-[28px] ${t.bg} p-7 md:p-8 transition-all duration-700 ${tipsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: `${150 + i * 100}ms` }}
              >
                <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
                  <tip.icon className="w-7 h-7 text-slate-900" />
                </span>
                <h3 className={`font-display text-xl md:text-2xl font-medium mb-2 ${t.big}`}>{tip.title}</h3>
                <p className={`leading-relaxed ${t.small}`}>{tip.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqRef} className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Frequently asked <span className="font-black">questions</span>
        </h2>
        <p className={`text-center text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Common questions about paystubs and apartment applications
        </p>

        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openFAQ === index}
              onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
            />
          ))}

          <div className="text-center mt-8">
            <button
              onClick={() => navigate("/faq")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
            >
              <HelpCircle className="w-4 h-4" />
              View All FAQs
            </button>
          </div>
        </div>
      </section>

      {/* Internal Links Section */}
      <section ref={linksRef} className="max-w-[1288px] mx-auto px-6 py-16 md:py-20">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${linksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Explore more <span className="font-black">document generators</span>
        </h2>
        <p className={`text-center text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${linksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          MintSlip offers a complete suite of professional document generators for all your financial documentation needs.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {internalLinks.map((link, idx) => (
            <div
              key={idx}
              className={`transition-all duration-700 ${linksInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${150 + idx * 60}ms` }}
            >
              <InternalLinkCard {...link} />
            </div>
          ))}
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-20">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 md:p-8">
            <div className="flex items-start gap-4">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-display font-bold text-slate-900 mb-2">Legal disclaimer</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">
                  MintSlip provides tools for creating income documentation and is intended for lawful and legitimate use only. The information provided on this page is for educational purposes and does not constitute legal or financial advice.
                </p>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">
                  Users are solely responsible for ensuring that all information entered into MintSlip accurately reflects their actual income and employment status. Misrepresenting income or employment information on rental applications may constitute fraud and can result in criminal charges, civil liability, eviction, and other legal consequences.
                </p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Always provide truthful and accurate information to landlords and property managers. MintSlip does not verify the accuracy of user-entered information and is not responsible for how documents are used. By using MintSlip, you agree to our <Link to="/terms" className="text-emerald-700 hover:text-emerald-800 underline">Terms of Service</Link> and <Link to="/privacy" className="text-emerald-700 hover:text-emerald-800 underline">Privacy Policy</Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section ref={ctaRef} className="pb-20 md:pb-28 px-6 bg-white">
        <div className={`max-w-[1288px] mx-auto text-center transition-all duration-700 ${ctaInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Ready to create your <span className="font-black">professional paystub</span>?
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Create professional paystubs with accurate tax calculations in under five minutes. Our easy-to-use generator helps you document your income professionally for apartment applications.
          </p>

          <div className="flex justify-center mb-4">
            <button className="navbar-cta" onClick={() => navigate("/app/paystubs")}>
              <FileText size={18} />
              <span>Create a paystub</span>
            </button>
          </div>
          <button
            type="button"
            onClick={() => openSupportChat()}
            className="text-slate-600 underline underline-offset-4 hover:text-emerald-700 transition-colors"
          >
            Contact Support
          </button>

          <div className="flex flex-wrap items-center justify-center gap-8 mt-12 text-slate-500 text-sm">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-700" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-emerald-700" />
              <span>Instant Download</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-700" />
              <span>No Account Required</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
